import { create } from "zustand";
import {
  ForecastResponse,
  IncomeHourlyResponse,
  getForecast,
  getIncomeHourly,
} from "@/api/kefc";
import { CityKey } from "@/features/services";

type Status = "idle" | "loading" | "ready" | "error";

interface Slot<T> {
  status: Status;
  data?: T;
  error?: string;
  loadedAt?: number;
}

interface CompareState {
  forecasts: Partial<Record<CityKey, Slot<ForecastResponse>>>;
  /** Заказы по часам за окно — один ответ на все три города. */
  income: Slot<IncomeHourlyResponse>;
  /** Курсор на графике: индекс окна; null — следовать за текущим часом. */
  hour: number | null;
  setHour: (hour: number | null) => void;
  loadForecast: (city: CityKey, force?: boolean) => Promise<void>;
  loadIncome: (force?: boolean) => Promise<void>;
}

// Такт данных Яндекса — 10 минут; чаще спрашивать нечего.
const TTL_MS = 10 * 60e3;
// Окно для типового профиля спроса: неделя ловит будни и выходные, а ответ
// остаётся лёгким (~130 КБ против 330 за две недели).
const INCOME_DAYS = 7;

const fresh = <T,>(slot?: Slot<T>) =>
  slot?.status === "ready" && slot.loadedAt && Date.now() - slot.loadedAt < TTL_MS;

const errorText = (e: unknown) =>
  e instanceof Error ? e.message : "Не удалось загрузить данные";

export const useCompareStore = create<CompareState>()((set, get) => ({
  forecasts: {},
  income: { status: "idle" },
  hour: null,
  setHour: (hour) => set({ hour }),

  loadForecast: async (city, force = false) => {
    const slot = get().forecasts[city];
    if (!force && (fresh(slot) || slot?.status === "loading")) return;
    set((s) => ({
      forecasts: { ...s.forecasts, [city]: { ...slot, status: "loading" } },
    }));
    try {
      const data = await getForecast(city);
      set((s) => ({
        forecasts: {
          ...s.forecasts,
          [city]: { status: "ready", data, loadedAt: Date.now() },
        },
      }));
    } catch (e) {
      set((s) => ({
        forecasts: {
          ...s.forecasts,
          [city]: { ...slot, status: "error", error: errorText(e) },
        },
      }));
    }
  },

  loadIncome: async (force = false) => {
    const slot = get().income;
    if (!force && (fresh(slot) || slot.status === "loading")) return;
    set({ income: { ...slot, status: "loading" } });
    try {
      const data = await getIncomeHourly(INCOME_DAYS);
      set({ income: { status: "ready", data, loadedAt: Date.now() } });
    } catch (e) {
      set({ income: { ...slot, status: "error", error: errorText(e) } });
    }
  },
}));
